// The waveform lane (P2.6) — UX §3.1's ribbon and overview, and UX §5.4's seek rules.
//
// **React draws nothing here and re-renders for nothing here.** UX §7: "meters, waveforms, jog
// rings update at display rate outside the animation system entirely (they're data, not motion
// design)", which is the discipline P0.7's meter established and Stack §2 states as a rule — "no
// React state in any rAF or SAB-reading path". This component's whole job is to create a canvas,
// hand it to a worker, and translate clicks. The playhead moves sixty times a second without React
// learning about it.
//
// The canvas is created imperatively rather than as JSX for the reason App.tsx already documents:
// `transferControlToOffscreen` is **one-way and permanent**, so a canvas element reused across
// effect runs would be transferred twice and throw. StrictMode remounts effects deliberately to
// surface exactly this.

import { useCallback, useEffect, useRef, type JSX } from 'react';

import {
  regionAt,
  seekAllowed,
  type WaveformOrientation,
  type WaveformTheme,
} from '../../platform/types/waveform-render';
import { createWaveformHost, type WaveformHost } from '../../visual/waveform';
import { SIGNAL_SOURCE, type SignalSourceIndex } from '../../platform/types/rings';
import type { DeckId } from '../../platform/types/control';
import type { TrackLoaderApi } from './useTrackLoader';

/**
 * Reads a design token and converts it to the 0–1 RGB the shader wants.
 *
 * The worker has no DOM and therefore no `getComputedStyle`, so tokens are resolved here and passed
 * across. UX §5.1's rule survives intact — the *component* still asks for `--deck-a` by name, and a
 * theme change at M7 changes what that resolves to. A hex literal inside the renderer would opt the
 * waveform out of every theme silently, which is precisely what the token architecture exists to
 * prevent.
 */
function readColourToken(
  name: string,
  fallback: [number, number, number],
): [number, number, number] {
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  const hex = /^#([\da-f]{6})$/i.exec(raw);
  if (hex === null) {
    // Tokens are authored as six-digit hex (tokens.css). Anything else — a theme using rgb(), a
    // property that failed to resolve — takes the fallback rather than drawing a black waveform.
    return fallback;
  }
  const value = Number.parseInt(hex[1] ?? '', 16);
  return [((value >> 16) & 255) / 255, ((value >> 8) & 255) / 255, (value & 255) / 255];
}

function readTheme(deckId: DeckId): WaveformTheme {
  // UX §5.1: identity applies "consistently to each deck's waveform …", so a lane asks for *its*
  // deck's token. Hard-coded to `--deck-a` until P3.13, which was invisible while deck B had no
  // waveform and would have drawn both lanes pink the moment it got one.
  const deck =
    deckId === 'a'
      ? readColourToken('--deck-a', [1, 0.36, 0.53])
      : readColourToken('--deck-b', [0.27, 0.85, 1]);
  return {
    envelope: deck,
    // The RMS core, brighter than the envelope so the body of the sound reads inside its reach.
    // Derived from the deck colour rather than being its own token: it must stay the deck's
    // identity under every theme, and a second token could drift away from the first.
    core: [
      Math.min(1, deck[0] * 0.45 + 0.55),
      Math.min(1, deck[1] * 0.45 + 0.55),
      Math.min(1, deck[2] * 0.45 + 0.55),
    ],
    playhead: readColourToken('--text-hi', [0.93, 0.94, 1]),
  };
}

export interface WaveformLaneProps {
  readonly loader: TrackLoaderApi;
}

/**
 * Which deck's block in the FR-26 signal frame a lane reads, and which way up it draws (P3.13).
 *
 * Derived from `deckId` rather than passed, so a lane cannot be given deck B's loader and deck A's
 * source — the bug that produces a waveform scrolling to the wrong deck's playhead, which looks
 * almost right and is unfindable by reading either component on its own. Same argument
 * `TrackLoaderApi.deckId` makes for the pitch fader.
 */
function laneFor(deckId: DeckId): {
  source: SignalSourceIndex;
  orientation: WaveformOrientation;
} {
  return deckId === 'a'
    ? { source: SIGNAL_SOURCE.DECK_A, orientation: 'mirrored' }
    : { source: SIGNAL_SOURCE.DECK_B, orientation: 'normal' };
}

export function WaveformLane({ loader }: WaveformLaneProps): JSX.Element {
  const lane = laneFor(loader.deckId);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const hostRef = useRef<WaveformHost | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  // Read inside a pointer handler, which must not be rebuilt every time the transport changes —
  // a ref keeps the handler stable while still seeing the current value.
  const playingRef = useRef(loader.playing);
  playingRef.current = loader.playing;

  // The host, created once per mount. Not re-created when a track arrives: uploading a texture is
  // a message, and tearing down a GL context per track would be absurd.
  useEffect(() => {
    const container = containerRef.current;
    if (container === null) {
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.className = 'waveform__canvas';
    container.append(canvas);
    canvasRef.current = canvas;

    const bounds = container.getBoundingClientRect();
    // Capped at 2, exactly as App.tsx caps the scene's: a 3x screen is 9x the fragment work for a
    // difference nobody can see, and NFR-P3's 60 FPS floor is measured in pixels shaded.
    const pixelRatio = Math.min(globalThis.devicePixelRatio, 2);

    let host: WaveformHost | null = null;
    try {
      host = createWaveformHost({
        canvas,
        width: Math.max(1, bounds.width),
        height: Math.max(1, bounds.height),
        pixelRatio,
        theme: readTheme(loader.deckId),
        sourceIndex: lane.source,
        orientation: lane.orientation,
      });
    } catch (cause: unknown) {
      // PRD §6.3: no feature may crash Tier 2. A browser that cannot give us WebGL2 gets a lane
      // with no waveform in it, not a broken app — and the console says why.
      console.error('[synapse] waveform display unavailable', cause);
    }
    hostRef.current = host;

    // The lane is a grid row, so it changes width with the window and its drawing buffer has to
    // follow. `ResizeObserver` rather than a window listener: the lane can change size without the
    // window doing so (a panel collapsing at M4), and this sees both.
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry === undefined) {
        return;
      }
      const { width, height } = entry.contentRect;
      hostRef.current?.resize(
        Math.max(1, width),
        Math.max(1, height),
        Math.min(globalThis.devicePixelRatio, 2),
      );
    });
    observer.observe(container);

    return () => {
      observer.disconnect();
      host?.dispose();
      hostRef.current = null;
      canvas.remove();
      canvasRef.current = null;
    };
  }, [loader.deckId, lane.source, lane.orientation]);

  // A track finished analysing. One message, two texture uploads in the worker, done.
  useEffect(() => {
    const host = hostRef.current;
    const { analysis, signals, frameCount, sampleRateHz } = loader;

    if (host === null) {
      return;
    }
    if (
      analysis === null ||
      signals === null ||
      frameCount === null ||
      sampleRateHz === null
    ) {
      host.clearTrack();
      return;
    }

    host.setTrack(analysis.mipmaps, signals, frameCount, sampleRateHz);
  }, [loader.analysis, loader.signals, loader.frameCount, loader.sampleRateHz, loader]);

  /**
   * The beatgrid to tick (P3.13) — UX §3.1's "beat ticks".
   *
   * **`loader.beatGrid`, which is already the grid the deck is *using*** — the detector's, or the
   * human's correction since P3.12. That is the whole point of drawing it: FR-13 hands the user
   * tools to move a grid and, until this, nothing to see the result on. P3.5's owed check ("does the
   * grid line up?") is not a question a number can answer.
   *
   * Its own effect, keyed on the grid alone, so a tap on the Tap button costs one small message and
   * not a mipmap re-upload.
   *
   * **Bar lines only once a human has placed the anchor.** Nothing in the pipeline detects a
   * downbeat — Architecture §5 stops at a constant-tempo grid — so "beat one is the anchor" is a
   * guess the app is not entitled to draw as a fact. When the user has tapped or nudged it
   * themselves, it is *their* claim and the bars help them read it.
   */
  useEffect(() => {
    hostRef.current?.setGrid(
      loader.beatGrid === null
        ? null
        : {
            anchorSeconds: loader.beatGrid.anchorSeconds,
            beatSeconds: loader.beatGrid.beatSeconds,
            showBars: loader.gridOverride !== null,
          },
    );
  }, [
    loader.beatGrid?.anchorSeconds,
    loader.beatGrid?.beatSeconds,
    loader.gridOverride,
    loader.beatGrid,
  ]);

  /**
   * UX §5.4's seek rules, applied to a click.
   *
   * The x position is where in the *displayed range* the pointer landed, and the two displays show
   * different ranges: the overview shows the whole track, so x maps straight to a fraction of it.
   * The ribbon shows a window centred on the playhead — but the ribbon only seeks while paused, and
   * P2.6 keeps that mapping simple by treating a paused ribbon click as a seek within the visible
   * window. Scrubbing and dragging are P2.7's.
   */
  const onPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>): void => {
      const container = containerRef.current;
      if (container === null || loader.frameCount === null) {
        return;
      }

      const bounds = container.getBoundingClientRect();
      const region = regionAt(
        (event.clientY - bounds.top) / bounds.height,
        lane.orientation,
      );
      if (!seekAllowed(region, playingRef.current)) {
        return;
      }

      const x = Math.min(1, Math.max(0, (event.clientX - bounds.left) / bounds.width));

      if (region === 'overview') {
        loader.seekToFraction(x);
      }
    },
    [loader, lane.orientation],
  );

  return (
    <div
      ref={containerRef}
      className="waveform"
      onPointerDown={onPointerDown}
      // The lane is a control surface, so it says so. The ribbon's own seek rule is stated in the
      // label rather than left for a user to discover by having a track jump under them.
      role="group"
      aria-label={`Deck ${loader.deckId.toUpperCase()} waveform — click the overview strip to seek`}
    >
      {loader.analysis === null && (
        <span className="waveform__empty">
          {loader.phase === 'empty' ? 'Drop a track onto the deck' : 'Waveform'}
        </span>
      )}
    </div>
  );
}
